import { ArrowRightAlt } from "@mui/icons-material";
import Card from "../components/card/card";
import {
  ButtonFooter,
  RescheduleContainerStyled,
  TimeChangeRow,
} from "./reschedule-confirmation.styles";
import { DangerButton, PrimaryButton } from "../../../components/button/button";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  cancelRescheduleNofity,
  confirmReschedule,
  getRescheduleData,
} from "../../../actions";
import { addNotification } from "../../../store/slices/ui-slice";
import { v4 as uuid } from "uuid";
import { useAppDispatch } from "../../../store/hooks";
import Loader from "../../../components/loader/loader";

interface RescheduleData {
  reschedule_token: string;
  old_start: string;
  old_end: string;
  new_start: string;
  new_end: string;
  is_available: boolean;
}

const RescheduleConfirmation: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  console.log("Here is the token", token);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [data, setData] = useState<RescheduleData | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [confirmDisabled, setConfirmDisabled] = useState(false);

  useEffect(() => {
    if (!token) return;

    let isMounted = true;

    const fetchData = async () => {
      try {
        const res = await getRescheduleData(token);
        const { success, data } = res.data;
        if (!success || !data) {
          if (isMounted) navigate("/", { replace: true });
          return;
        }
        if (isMounted) setData(data);
      } catch (err: any) {
        dispatch(
          addNotification({
            id: uuid(),
            type: "error",
            message: "Something went wrong while fetching data",
          })
        );
        if (isMounted) navigate("/", { replace: true });
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [token, dispatch, navigate]);

  const handleConfirm = async () => {
    if (!token) return;

    try {
      setConfirming(true);
      const res = await confirmReschedule(token);
      if (res?.data?.success) {
        dispatch(
          addNotification({
            id: uuid(),
            type: "success",
            message: "Reschedule confirmed successfully.",
          })
        );
        navigate("/");
      } else {
        setConfirmDisabled(true);
        dispatch(
          addNotification({
            id: uuid(),
            type: "error",
            message: res?.data?.message || "Failed to confirm reschedule",
          })
        );
        // Redirect on failure
        navigate("/", { replace: true });
      }
    } catch (err: any) {
      setConfirmDisabled(true);
      dispatch(
        addNotification({
          id: uuid(),
          type: "error",
          message: "Failed to confirm reschedule",
        })
      );
      // Redirect on error
      navigate("/", { replace: true });
    } finally {
      setConfirming(false);
    }
  };

  const handleCancelNotify = async () => {
    if (!token) return;

    try {
      setCancelling(true);
      const res = await cancelRescheduleNofity(token);
      if (res?.data?.success) {
        dispatch(
          addNotification({
            id: uuid(),
            type: "success",
            message: "Reschedule canceled without notifying.",
          })
        );
        navigate("/");
      } else {
        dispatch(
          addNotification({
            id: uuid(),
            type: "error",
            message: res?.data?.message || "Failed to cancel reschedule",
          })
        );
        // Redirect on failure
        navigate("/", { replace: true });
      }
    } catch (err: any) {
      dispatch(
        addNotification({
          id: uuid(),
          type: "error",
          message: "Failed to cancel reschedule",
        })
      );
      // Redirect on error
      navigate("/", { replace: true });
    } finally {
      setCancelling(false);
    }
  };

  const formatDateTime = (datetime: any) =>
    new Date(datetime).toLocaleString("en-US", {
      day: "2-digit",
      month: "short",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

  console.log({ loading, confirming, data });

  return (
    <RescheduleContainerStyled>
      {loading ? (
        <Loader absolute />
      ) : (
        <div className="main-wrapper">
          <div className="reschedule-container-wrapper">
            <Card
              title={"Reschedule appointment"}
              description={
                <TimeChangeRow>
                  <strong>
                    {(data?.old_start && formatDateTime(data?.old_start)) ||
                      "23 Dec at 3:00 PM"}{" "}
                  </strong>
                  <ArrowRightAlt />
                  <strong>
                    {(data?.new_start && formatDateTime(data?.new_start)) ||
                      "24 Dec at 1:00 PM"}
                  </strong>
                </TimeChangeRow>
              }
              btnTitle={"Continue"}
              customFooter={
                <ButtonFooter>
                  <DangerButton
                    disabled={cancelling || confirming}
                    isLoading={cancelling}
                    variant="outlined"
                    onClick={handleCancelNotify}
                  >
                    Don't Notify Me Again
                  </DangerButton>
                  <PrimaryButton
                    disabled={confirming || cancelling || !data?.is_available || confirmDisabled}
                    isLoading={confirming}
                    onClick={handleConfirm}
                  >
                    Reschedule for this Time
                  </PrimaryButton>
                </ButtonFooter>
              }
            >
              {data?.is_available ? (
                <div>
                  If you want to reschedule your appointment, click the "Reschedule for this Time" button below. It is possible that
                  another client can claim this slot before you do, so please confirm quickly.
                  Do you really want to confirm it?
                </div>
              ) : (
                <div>
                  Unfortunately, this reschedule opportunity has already been taken by another client. You will be notified again if another opportunity becomes available, unless you choose to cancel that notification here on this page. Thank you for your understanding.
                </div>
              )}
            </Card>
          </div>
        </div>
      )}
    </RescheduleContainerStyled>
  );
};

export default RescheduleConfirmation;
